// Pure, i18n-free helpers for the Poola "nearest-to-finish" offer sort + the
// completion section bands (P4-A). No BE dependency, no reactivity — they operate
// on plain offer objects. In production the objects are PoolaOffer model
// instances, so the `isComplete` / `isNearComplete` getters are the SINGLE SOURCE
// OF TRUTH for band membership (see src/models/commerce/PoolaOffer.js) — these
// helpers never re-derive the completion thresholds themselves.
//
// STABILITY NOTE (why these live behind usePoolaOffers.resort(), not the `offers`
// computed): the sort MUST NOT re-run on every realtime `.PoolaOfferClaimed` stock
// patch — that would reshuffle cards under a scrolling thumb. usePoolaOffers
// freezes the OUTPUT of these helpers into an id order and only rebuilds it on
// discrete events (load / refresh / supplier-switch); a live patch updates card
// content in place but never the slot.

// Render order of the three bands, top → bottom. 'regular' carries no header.
export const POOLA_BAND_KEYS = ['nearComplete', 'regular', 'completed'];

/** Claimed fraction of an offer (0 for an empty/malformed 0-piece offer). */
function claimedRatio(offer) {
  const total = offer.piecesTotal ?? 0;
  if (!(total > 0)) return 0; // guard divide-by-zero
  return (offer.piecesClaimed ?? 0) / total;
}

/**
 * Stable comparator — nearest-to-finish first, completed offers sunk to the
 * bottom. Priority:
 *   1. live before complete (complete sinks)
 *   2. among live: higher claimed ratio first (closest to completing)
 *   3. tie on ratio: fewer pieces remaining first
 *   4. fully tied: 0 → Array.prototype.sort (stable since ES2019) preserves the
 *      caller's order, i.e. the server cursor order.
 */
export function compareOffers(a, b) {
  const aComplete = a.isComplete ? 1 : 0;
  const bComplete = b.isComplete ? 1 : 0;
  if (aComplete !== bComplete) return aComplete - bComplete;
  // Both complete → equal for ordering (remaining is 0 for both); keep cursor order.
  if (aComplete === 1) return 0;

  const ratioA = claimedRatio(a);
  const ratioB = claimedRatio(b);
  if (ratioA !== ratioB) return ratioB - ratioA; // descending

  const remainingA = a.piecesRemaining ?? 0;
  const remainingB = b.piecesRemaining ?? 0;
  if (remainingA !== remainingB) return remainingA - remainingB; // ascending

  return 0; // stable sort keeps server cursor order
}

/**
 * Sort `list` nearest-to-finish and partition it into the three completion
 * bands, preserving the sorted order WITHIN each band. Returns offer arrays (not
 * ids), so callers/tests can read either. Does not mutate `list`.
 *
 * Band membership uses each offer's OWN completion getters, so a low-stock offer
 * under 80% claimed still lands in «قربوا يكملوا», matching the card's own
 * urgency skin. Because the bands are emitted top→bottom as
 * [nearComplete, regular, completed], a near-complete offer always renders above
 * a lower-ratio regular one even when their raw ratios would interleave.
 *
 * @param {Array<{id:string, isComplete:boolean, isNearComplete:boolean,
 *   piecesTotal:number, piecesClaimed:number, piecesRemaining:number}>} list
 * @returns {{ nearComplete: object[], regular: object[], completed: object[] }}
 */
export function sortAndGroupOffers(list) {
  const sorted = [...list].sort(compareOffers);
  const bands = { nearComplete: [], regular: [], completed: [] };
  for (const offer of sorted) {
    if (offer.isComplete) bands.completed.push(offer);
    else if (offer.isNearComplete) bands.nearComplete.push(offer);
    else bands.regular.push(offer);
  }
  return bands;
}
